import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  applicationSourceLabel,
  bytesOf,
  DIRECT_SOURCE,
  NUMBER_FORMAT_LOCALES,
  setDisplayLocale,
  setFileLimits,
  setFiscalYearStartMonth,
  recordableSourceOptions,
  setSourceLabels,
  SUPPORTED_CURRENCIES,
  type AppSettingsView,
} from '@nest-recruit/shared';
import { ApiError, apiGet } from './api-client';
import { setDisplayFormats } from './format';

// S31 (ADMIN-15, ADMIN-08): the organisation's settings every screen formats with. Read once at
// sign-in, beside the session, and again after an HR Lead changes them — never per screen.

export const APP_SETTINGS_KEY = ['app-settings'] as const;

/** Makes the shared helpers (money, dates, numbers, fiscal year, upload limits, source names) follow the settings. */
export function applyAppSettings(view: AppSettingsView): void {
  const numberLocale = NUMBER_FORMAT_LOCALES[view.settings.numberFormat];
  setDisplayLocale(numberLocale);
  setDisplayFormats({ timeZone: view.settings.defaultTimezone, datePattern: view.settings.dateFormat, numberLocale });
  setFiscalYearStartMonth(view.settings.fiscalYearStartMonth);
  setFileLimits({ cvMaxBytes: bytesOf(view.settings.cvMaxMb), documentMaxBytes: bytesOf(view.settings.documentMaxMb) });
  setSourceLabels(new Map(view.sources.map((source) => [source.key, source.label])));
}

export async function fetchAppSettings(): Promise<AppSettingsView> {
  const view = await apiGet<AppSettingsView>('/settings');
  applyAppSettings(view);
  return view;
}

export function useAppSettings(): UseQueryResult<AppSettingsView, ApiError> {
  return useQuery<AppSettingsView, ApiError>({
    queryKey: APP_SETTINGS_KEY,
    queryFn: fetchAppSettings,
    staleTime: Number.POSITIVE_INFINITY,
    placeholderData: (previous) => previous,
  });
}

export interface SourceOption {
  readonly value: string;
  readonly label: string;
}

/**
 * ADMIN-08 in a picker. `record`: what a person can be recorded as coming from — the visible
 * sources a human picks. `filter`: every source, hidden ones included, because history keeps them.
 */
export function useSourceOptions(purpose: 'record' | 'filter'): readonly SourceOption[] {
  const settings = useAppSettings();
  const sources = settings.data?.sources;
  if (!sources) return purpose === 'record' ? recordableSourceOptions : [...recordableSourceOptions, { value: DIRECT_SOURCE, label: applicationSourceLabel(DIRECT_SOURCE) }];
  const shown = purpose === 'record' ? sources.filter((source) => !source.isHidden && source.kind !== 'system') : sources;
  return shown.map((source) => ({ value: source.key, label: source.label }));
}

/** ADMIN-15 (Q20): the currencies switched on, for a money picker. */
export function useCurrencyOptions(): readonly SourceOption[] {
  const settings = useAppSettings();
  const enabled: readonly string[] = settings.data?.settings.currencies ?? SUPPORTED_CURRENCIES;
  return enabled.map((code) => ({ value: code, label: code }));
}

/** A value already on the record stays offered even if its currency was switched off since. */
export function withCurrent(options: readonly SourceOption[], current: string | null | undefined): readonly SourceOption[] {
  if (!current || options.some((option) => option.value === current)) return options;
  return [...options, { value: current, label: current }];
}
